/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const formatWhen = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

const statusClass = (status) => (status ? status.toLowerCase().replace(/\s+/g, '-') : '');

const ACTIVE_STATUSES = ['Approved', 'In Progress', 'Requires Extension'];
const REVISION_STATUSES = ['Revision Required', 'Revised'];
const COMPLETED_STATUSES = ['Completed', 'System Removal', 'Expired', 'Terminated', 'Rejected'];

const EVENT_ICONS = {
  application_submitted: '\uD83D\uDCCB',
  application_approved: '\u2705',
  application_rejected: '\u274C',
  revision_requested: '\uD83D\uDD04',
  application_revised: '\uD83D\uDCDD',
  chamber_assigned: '\uD83C\uDFE0',
  chamber_updated: '\uD83D\uDD27',
  chamber_completed: '\u2705',
  chamber_deleted: '\uD83D\uDDD1\uFE0F',
  resource_assigned: '\uD83D\uDCE6',
  resource_updated: '\uD83D\uDD27',
  invoice_created: '\uD83D\uDCCB',
  invoice_status_changed: '\uD83D\uDCCA',
  invoice_edited: '\u270F\uFE0F',
  invoice_deleted: '\uD83D\uDDD1\uFE0F',
  payment_recorded: '\uD83D\uDCB0',
  request_submitted: '\uD83D\uDCE9',
  request_completed: '\u2705',
  request_approved: '\u2705',
  request_status_changed: '\uD83D\uDD04',
  request_client_comment: '\uD83D\uDCAC',
  request_rejected: '\u274C',
  general_comment: '\uD83D\uDCAC',
  team_member_added: '\uD83D\uDC65',
  team_member_removed: '\uD83D\uDC64',
  team_role_changed: '\uD83D\uDD11',
  invitation_accepted: '\uD83E\uDD1D',
  invitation_declined: '\uD83D\uDC4B',
  project_renewed: '\uD83D\uDD04',
  project_renewal_confirmation: '\u23F0',
  project_started: '\uD83D\uDE80',
  project_completed: '\uD83C\uDFC1',
  project_terminated: '\u26D4',
};

const sortProjects = (projects) => {
  const rank = (status) => {
    if (ACTIVE_STATUSES.includes(status)) return 0;
    if (REVISION_STATUSES.includes(status) || status === 'Submitted') return 1;
    if (status === 'Saved') return 2;
    if (COMPLETED_STATUSES.includes(status)) return 3;
    return 4;
  };
  return [...projects].sort((a, b) => {
    const r = rank(a.project_status) - rank(b.project_status);
    if (r !== 0) return r;
    const aDate = new Date(a.project_end_date || a.updatedAt || 0).getTime();
    const bDate = new Date(b.project_end_date || b.updatedAt || 0).getTime();
    return bDate - aDate;
  });
};

const projectOpenPath = (proj) => {
  if (REVISION_STATUSES.includes(proj.project_status)) {
    return `/projects/revise/${proj._id}`;
  }
  if (proj.project_status === 'Saved') return '/projects/saved';
  if (proj.project_status === 'Submitted') return '/projects/submitted';
  return `/projects/view/${proj._id}`;
};

/**
 * One row per project (active first, then completed). Each row offers Open
 * Project, Team Chat (project chat tab), and a notification button that
 * expands the project's unread/new notifications inline.
 */
const MyProjectsPanel = ({
  projects = [],
  user,
  notifications = [],
  onUnreadTotalChange,
  onNotificationClick,
  onNotificationsChanged
}) => {
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [loadingChats, setLoadingChats] = useState(true);
  const [expandedProjectId, setExpandedProjectId] = useState(null);

  const fetchChats = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/team-chats');
      setChats(data || []);
    } catch (err) {
      console.error('Error fetching team chats:', err);
    } finally {
      setLoadingChats(false);
    }
  }, []);

  useEffect(() => {
    fetchChats();
    const interval = setInterval(fetchChats, 30000);
    return () => clearInterval(interval);
  }, [fetchChats]);

  useEffect(() => {
    if (!onUnreadTotalChange) return;
    const chatUnread = chats.reduce((sum, chat) => sum + (chat.unreadCount || 0), 0);
    const notifUnread = notifications.filter(n => !n.is_read).length;
    onUnreadTotalChange({ chat: chatUnread, notifications: notifUnread });
  }, [chats, notifications, onUnreadTotalChange]);

  const chatByProject = new Map(chats.map(c => [String(c.projectId), c]));
  const notifsByProject = new Map();
  notifications.forEach(n => {
    const pid = String(n.project?._id || n.project || '');
    if (!pid) return;
    if (!notifsByProject.has(pid)) notifsByProject.set(pid, []);
    notifsByProject.get(pid).push(n);
  });

  const sorted = sortProjects(projects);

  if (sorted.length === 0) {
    return (
      <div className="home-notif-empty">
        No projects yet.{' '}
        <Link to="/projects/new" style={{ color: '#475569', fontWeight: 600 }}>
          Create a new application
        </Link>
      </div>
    );
  }

  const handleOpenProject = (proj) => {
    const path = projectOpenPath(proj);
    if (path.startsWith('/projects/view/')) {
      navigate(path);
    } else {
      navigate(path);
    }
  };

  const handleTeamChat = (proj) => {
    if (REVISION_STATUSES.includes(proj.project_status)) {
      navigate(`/projects/revise/${proj._id}`);
      return;
    }
    navigate(`/projects/view/${proj._id}?tab=chat`);
  };

  const handleToggleNotifs = (projId) => {
    setExpandedProjectId(prev => (String(prev) === String(projId) ? null : projId));
  };

  const handleNotifItemClick = async (notif) => {
    if (onNotificationClick) {
      onNotificationClick(notif);
    }
  };

  const handleMarkProjectRead = async (projId, e) => {
    e.stopPropagation();
    try {
      await axios.patch(`/api/notifications/read-project/${projId}`);
      if (onNotificationsChanged) onNotificationsChanged();
    } catch (err) {
      console.error('Failed to mark project notifications as read:', err);
    }
  };

  return (
    <div className="home-project-list">
      {sorted.map(proj => {
        const chat = chatByProject.get(String(proj._id));
        const chatUnread = chat?.unreadCount || 0;
        const projNotifs = notifsByProject.get(String(proj._id)) || [];
        const unreadNotifs = projNotifs.filter(n => !n.is_read);
        const unreadCount = unreadNotifs.length;
        const expanded = String(expandedProjectId) === String(proj._id);
        const isCompleted = COMPLETED_STATUSES.includes(proj.project_status);

        return (
          <div
            key={proj._id}
            className={`home-project-card ${unreadCount > 0 || chatUnread > 0 ? 'unread' : ''} ${isCompleted ? 'completed' : ''} ${expanded ? 'expanded' : ''}`}
          >
            <div className="home-project-row">
              <div className="home-project-info">
                <div className="home-project-title-row">
                  <span className="home-project-title">{proj.project_title || 'Untitled Project'}</span>
                  <span className={`home-project-status ${statusClass(proj.project_status)}`}>
                    {proj.project_status}
                  </span>
                </div>
                <div className="home-project-id">{proj.project_id || ''}</div>
                <div className="home-project-detail">
                  {proj.facility?.facility_name || ''}
                  {proj.project_end_date ? ` \u00B7 Ends ${new Date(proj.project_end_date).toLocaleDateString()}` : ''}
                </div>
                {chat && chat.lastMessageText && (
                  <div className="home-project-chat-preview">
                    {`${chat.lastMessageSender ? `${chat.lastMessageSender}: ` : ''}${chat.lastMessageText}`}
                    {chat.lastMessageAt && (
                      <span className="home-project-chat-time">{formatWhen(chat.lastMessageAt)}</span>
                    )}
                  </div>
                )}
              </div>
              <div className="home-project-actions">
                <button
                  className="home-project-btn open"
                  onClick={() => handleOpenProject(proj)}
                >
                  Open Project
                </button>
                {chat || ACTIVE_STATUSES.includes(proj.project_status) || REVISION_STATUSES.includes(proj.project_status) ? (
                  <button
                    className={`home-project-btn chat ${chatUnread > 0 ? 'unread' : ''}`}
                    onClick={() => handleTeamChat(proj)}
                    disabled={loadingChats && !chat && !REVISION_STATUSES.includes(proj.project_status)}
                  >
                    Team Chat
                    {chatUnread > 0 && <span className="home-project-btn-badge">{chatUnread}</span>}
                  </button>
                ) : (
                  <span className="home-project-chat-na" title={loadingChats ? 'Loading chats' : 'Team chat is available once the project is active'}>
                    {loadingChats ? '\u2026' : 'No team chat'}
                  </span>
                )}
                <button
                  className={`home-project-btn notif ${unreadCount > 0 ? 'unread' : ''} ${expanded ? 'active' : ''}`}
                  onClick={() => handleToggleNotifs(proj._id)}
                  aria-expanded={expanded}
                  title={expanded ? 'Collapse notifications' : (unreadCount > 0 ? `${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}` : 'Show notifications')}
                >
                  Notifications
                  {unreadCount > 0 && <span className="home-project-btn-badge">{unreadCount}</span>}
                  <span className="home-project-btn-chevron" aria-hidden="true">{expanded ? '\u25B2' : '\u25BC'}</span>
                </button>
              </div>
            </div>

            {expanded && (
              <div className="home-project-notifs">
                <div className="home-project-notifs-header">
                  <span>
                    {projNotifs.length === 0
                      ? 'No notifications for this project'
                      : `${projNotifs.length} notification${projNotifs.length === 1 ? '' : 's'}`}
                    {unreadCount > 0 ? ` \u00B7 ${unreadCount} unread` : ''}
                  </span>
                  {unreadCount > 0 && (
                    <button
                      type="button"
                      className="home-section-action-btn"
                      onClick={(e) => handleMarkProjectRead(proj._id, e)}
                    >
                      Mark all read
                    </button>
                  )}
                </div>
                {projNotifs.length > 0 && (
                  <ul className="home-notif-list">
                    {projNotifs.map(n => (
                      <li
                        key={n._id}
                        className={`home-notif-item ${n.is_read ? '' : 'unread'}`}
                      >
                        <div
                          className="home-notif-clickable"
                          onClick={() => handleNotifItemClick(n)}
                        >
                          <span className="home-notif-icon">
                            {EVENT_ICONS[n.event_type] || '\uD83D\uDD14'}
                          </span>
                          <div className="home-notif-content">
                            <div className="home-notif-title">{n.title}</div>
                            {n.preview && <div className="home-notif-preview">{n.preview}</div>}
                            <div className="home-notif-meta">
                              <span className="home-notif-time">{formatWhen(n.createdAt)}</span>
                            </div>
                          </div>
                          {!n.is_read && <span className="home-notif-dot" />}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MyProjectsPanel;
